import { useCallback, useEffect, useRef, useState } from 'react';

import type { TableRequestState } from '@/types/table';
import { initialTableRequestState } from '@/types/table';

type TableRequestController = {
  requestState: TableRequestState;
  beginRequest: () => number;
  completeRequest: (requestId: number) => boolean;
  failRequest: (requestId: number, error: string) => boolean;
  cancelRequest: (requestId: number) => void;
  retryKey: number;
  retry: () => void;
};

/**
 * Coordinates table requests so slower, older responses cannot overwrite the
 * state of a newer filter or pagination request.
 */
const useTableRequestState = (hasData: boolean): TableRequestController => {
  const [requestState, setRequestState] = useState<TableRequestState>(initialTableRequestState);
  const [retryKey, setRetryKey] = useState(0);
  const requestSequence = useRef(0);
  const hasDataRef = useRef(hasData);
  const mountedRef = useRef(true);

  hasDataRef.current = hasData;

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestSequence.current += 1;
    };
  }, []);

  const beginRequest = useCallback(() => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;

    if (mountedRef.current) {
      setRequestState(hasDataRef.current ? { status: 'refreshing', error: null } : { status: 'loading', error: null });
    }

    return requestId;
  }, []);

  const completeRequest = useCallback((requestId: number) => {
    const isLatestRequest = mountedRef.current && requestId === requestSequence.current;
    if (isLatestRequest) setRequestState({ status: 'success', error: null });
    return isLatestRequest;
  }, []);

  const failRequest = useCallback((requestId: number, error: string) => {
    const isLatestRequest = mountedRef.current && requestId === requestSequence.current;
    if (isLatestRequest) {
      setRequestState({ status: 'error', error, hasStaleData: hasDataRef.current });
    }
    return isLatestRequest;
  }, []);

  const cancelRequest = useCallback((requestId: number) => {
    if (requestId === requestSequence.current) requestSequence.current += 1;
  }, []);

  const retry = useCallback(() => setRetryKey((current) => current + 1), []);

  return { requestState, beginRequest, completeRequest, failRequest, cancelRequest, retryKey, retry };
};

export default useTableRequestState;
